import { Users } from '../models/User.tsx';
import { BetDetails } from '../models/MyBetModel.tsx';
import { Transaction } from '../models/User.tsx';
import {
  getBetFiguresDate,
  getBetLossAmount,
  getBetWinAmount,
  isBetLossResult,
  isBetWinResult,
  isWithinDateRange,
} from './functions.tsx';
import { getAdminFiguresWeekdayIndex as getFiguresWeekdayIndex } from './adminFiguresTime.tsx';

export type AnyBet = BetDetails;
export type DailyTotals = Record<string, Record<string, number> & { weeklyTotal: number }>;

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export type AccountViewOption = 'Active' | 'With' | 'Big' | string;

/** Default Weekly Figures player list. A different choice lasts only for the browser session. */
export const DEFAULT_WEEKLY_ACCOUNT_VIEW: AccountViewOption = 'Active';

const WEEKLY_ACCOUNT_VIEW_SESSION_KEY = 'weeklyAccountView';
const WEEKLY_ACCOUNT_VIEW_OPTIONS = new Set(['Active', 'With', 'All', 'Big']);

export function readWeeklyAccountViewSession(): AccountViewOption {
  try {
    const stored = sessionStorage.getItem(WEEKLY_ACCOUNT_VIEW_SESSION_KEY);
    if (stored && WEEKLY_ACCOUNT_VIEW_OPTIONS.has(stored)) return stored;
  } catch {
    // ignore private mode
  }
  return DEFAULT_WEEKLY_ACCOUNT_VIEW;
}

export function writeWeeklyAccountViewSession(value: string): void {
  try {
    if (!WEEKLY_ACCOUNT_VIEW_OPTIONS.has(value)) return;
    sessionStorage.setItem(WEEKLY_ACCOUNT_VIEW_SESSION_KEY, value);
  } catch {
    // ignore private mode
  }
}

export function clearWeeklyAccountViewSession(): void {
  try {
    sessionStorage.removeItem(WEEKLY_ACCOUNT_VIEW_SESSION_KEY);
  } catch {
    // ignore private mode
  }
}

export function groupBetsByDay(
  playerBets: Record<string, AnyBet[]>,
  startDate: Date,
  endDate: Date
): DailyTotals {
  const dailyResults: DailyTotals = {};

  for (const playerId in playerBets) {
    const bets = playerBets[playerId] || [];
    if (!bets.length) continue;

    dailyResults[playerId] = { weeklyTotal: 0 };
    DAY_NAMES.forEach(day => {
      dailyResults[playerId][day] = 0;
    });

    bets.forEach(bet => {
      const betDate = getBetFiguresDate(bet);
      if (!betDate || !isWithinDateRange(betDate, startDate, endDate)) return;

      const dayIndex = getFiguresWeekdayIndex(betDate);
      const dayNameStr = DAY_NAMES[dayIndex];

      const reward = bet.reward ?? 0;
      const amount = bet.bet ?? 0;
      const result = bet.grader?.result;

      if (isBetWinResult(result)) {
        const winAmount = getBetWinAmount(reward, result);
        dailyResults[playerId][dayNameStr] += winAmount;
        dailyResults[playerId].weeklyTotal += winAmount;
      } else if (isBetLossResult(result) && bet.betType !== 'Free Play') {
        const lossAmount = getBetLossAmount(amount, result);
        dailyResults[playerId][dayNameStr] -= lossAmount;
        dailyResults[playerId].weeklyTotal -= lossAmount;
      }
    });

    dailyResults[playerId].weeklyTotal =
      dailyResults[playerId].weeklyTotal % 1 === 0
        ? dailyResults[playerId].weeklyTotal
        : Number(dailyResults[playerId].weeklyTotal.toFixed(2));

    DAY_NAMES.forEach(day => {
      if (dailyResults[playerId][day] % 1 !== 0) {
        dailyResults[playerId][day] = Number(dailyResults[playerId][day].toFixed(2));
      }
    });
  }

  return dailyResults;
}

export function groupTransactionsByDay(
  playerTransactions: Record<string, Transaction[]>,
  startDate: Date,
  endDate: Date
): DailyTotals {
  const dailyResults: DailyTotals = {};

  for (const playerId in playerTransactions) {
    const transactions = playerTransactions[playerId] || [];
    if (!transactions.length) continue;

    dailyResults[playerId] = { weeklyTotal: 0 };
    DAY_NAMES.forEach(day => {
      dailyResults[playerId][day] = 0;
    });

    transactions.forEach(tx => {
      let txDate: Date | null = null;

      if ((tx.createDate as { seconds?: number })?.seconds) {
        txDate = new Date((tx.createDate as { seconds: number }).seconds * 1000);
      } else if (tx.createDate instanceof Date) {
        txDate = tx.createDate;
      }

      if (!txDate) return;
      if (txDate < startDate || txDate > endDate) return;

      if (tx.transactionType !== 'CreditAdjustment' && tx.transactionType !== 'DebitAdjustment') {
        return;
      }

      const dayIndex = getFiguresWeekdayIndex(txDate);
      const dayName = DAY_NAMES[dayIndex];

      if (tx.transactionType === 'CreditAdjustment') {
        dailyResults[playerId][dayName] += tx.amount;
        dailyResults[playerId].weeklyTotal += tx.amount;
      }

      if (tx.transactionType === 'DebitAdjustment') {
        dailyResults[playerId][dayName] -= tx.amount;
        dailyResults[playerId].weeklyTotal -= tx.amount;
      }
    });

    DAY_NAMES.forEach(day => {
      if (dailyResults[playerId][day] % 1 !== 0) {
        dailyResults[playerId][day] = Number(dailyResults[playerId][day].toFixed(2));
      }
    });

    if (dailyResults[playerId].weeklyTotal % 1 !== 0) {
      dailyResults[playerId].weeklyTotal = Number(dailyResults[playerId].weeklyTotal.toFixed(2));
    }
  }

  return dailyResults;
}

export function filterWeeklyPlayers(
  players: Users[],
  selectedOption: AccountViewOption,
  playerBets: Record<string, AnyBet[]>
): Users[] {
  let filteredPlayers = players;

  if (selectedOption === 'With') {
    filteredPlayers = filteredPlayers.filter(player => player.balance !== 0);
  } else if (selectedOption === 'Active') {
    filteredPlayers = players.filter(player => {
      const bets = playerBets[player.id!] || [];
      return bets.length > 0;
    });
  } else if (selectedOption === 'Big') {
    filteredPlayers = filteredPlayers.filter(player => (player.balance ?? 0) > 400);
  }

  return [...filteredPlayers].sort((a, b) =>
    (a?.userName || '').localeCompare(b?.userName || '', undefined, { numeric: true, sensitivity: 'base' })
  );
}

export function getWeeklyDepositsWithdrawalsByPlayer(
  playerTransactions: Record<string, Transaction[]>,
  startDate: Date,
  endDate: Date
): Record<string, number> {
  const totals: Record<string, number> = {};

  for (const playerId in playerTransactions) {
    const transactions = playerTransactions[playerId] || [];
    let total = 0;

    transactions.forEach(tx => {
      let txDate: Date | null = null;

      if ((tx.createDate as { seconds?: number })?.seconds) {
        txDate = new Date((tx.createDate as { seconds: number }).seconds * 1000);
      } else if (tx.createDate instanceof Date) {
        txDate = tx.createDate;
      }

      if (!txDate) return;
      if (txDate < startDate || txDate > endDate) return;

      if (tx.transactionType === 'deposit') {
        total += tx.amount;
      }

      if (tx.transactionType === 'withdrawal') {
        total -= tx.amount;
      }
    });

    totals[playerId] = total;
  }

  return totals;
}

export function sortSubAgents(subAgents: Users[]): Users[] {
  return [...(subAgents || [])].sort((a, b) =>
    (a?.userName || '').localeCompare(b?.userName || '', undefined, { numeric: true, sensitivity: 'base' })
  );
}

export function sumPendingForPlayers(
  players: Users[],
  totalPendingPlayer: Record<string, number>
): number {
  return players.reduce((total, player) => total + (totalPendingPlayer[player.id!] || 0), 0);
}

export function sumPlayerBalances(players: Users[]): number {
  return players.reduce((sum, player) => sum + (player.balance ?? 0), 0);
}

export function sumDailyBalances(
  filteredPlayers: Users[],
  weekDayNames: string[],
  dailyBalances: DailyTotals,
  dailyTransactions: DailyTotals
): number[] {
  return weekDayNames.map(day =>
    filteredPlayers.reduce((total, player) => {
      const bet = dailyBalances[player.id!]?.[day] || 0;
      const tx = dailyTransactions[player.id!]?.[day] || 0;
      return total + bet + tx;
    }, 0)
  );
}

export function sumWeeklyCombined(
  filteredPlayers: Users[],
  dailyBalances: DailyTotals,
  dailyTransactions: DailyTotals
): number {
  return filteredPlayers.reduce((sum, player) => {
    const bet = dailyBalances[player.id!]?.weeklyTotal || 0;
    const tx = dailyTransactions[player.id!]?.weeklyTotal || 0;
    return sum + bet + tx;
  }, 0);
}
